import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Socket } from 'socket.io-client';
import clsx from 'clsx';
import ChatFooter from '../../components/ChatFooter';
import FullScreenError from '../../components/Error/FullScreen';
import SpinnerLoader from '../../components/Loader/Spinner';
import MessageWrapper from '../../components/MessageWrapper';
import { LOADING_TEXT } from '../../constants/conversation';
import ConversationProvider, { useConversation } from '../../contexts/conversation';
import { Client, Organisation } from '../../enums/common';
import { useNativeFooter } from '../../hooks/useNativeFooter';
import useSocket from '../../hooks/useSocket';
import { getCookieHelpers } from '../../utils/cookie';
import { getOrganisation } from '../../utils/common';
import ChatHeader from '../../components/ChatHeader';
import ConnectionLostBanner from '../../components/ConnectionLostBanner';

function ChatContent({ showHeader, renderBackground }: { showHeader: boolean; renderBackground: () => JSX.Element }) {
    const { reconnecting } = useConversation();

    return (
        <div className='flex flex-col items-center w-full h-full bg-charcoal-40 lg:pt-14 lg:px-20'>
            {renderBackground()}
            <div className='relative flex flex-col w-full max-w-5xl h-full lg:h-[calc(100%-10rem)] lg:shadow lg:rounded-lg lg:overflow-hidden'>
                {showHeader && <ChatHeader />}
                <div id='chat-wrapper' className='relative flex flex-col gap-10 grow pb-20 bg-white overflow-scroll'>
                    <ConnectionLostBanner visible={reconnecting} />
                    <MessageWrapper />
                </div>
                <ChatFooter />
            </div>
        </div>
    );
}

function Conversation() {
    const clientId = getCookieHelpers().getCookie('clientId');
    const showHeader = clientId !== Client.ABUSADHC && clientId !== Client.ABUSIOHC;
    const [searchParams] = useSearchParams();
    const { connect } = useSocket();
    const { unmount } = useNativeFooter();
    const [socket, setSocket] = useState<Socket>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        window.addEventListener('pagehide', unmount);

        let id = searchParams.get('bookingId');
        const params: Record<string, string> = {};

        if (!id) {
            setError(true);
            return () => window.removeEventListener('pagehide', unmount);
        }

        if (id.startsWith('null')) {
            id = nanoid();
            searchParams.set('bookingId', id);
        }

        searchParams.forEach((value, key) => {
            params[key] = value;
        });
        params.id = id;

        connect(params).then((socketInstance) => {
            setSocket(socketInstance);
            setLoading(false);
        });

        return () => window.removeEventListener('pagehide', unmount);
    }, []);

    const renderBackground = () => {
        const className = 'absolute top-0 w-full h-72';
        const org = getOrganisation();

        switch (org) {
            case Organisation.ABHIBUS: {
                return <div className={clsx(className, 'bg-[#C32629]')} />;
            }
            case Organisation.CONFIRMTKT: {
                return <div className={clsx(className, 'bg-[#43A64E]')} />;
            }
            default: {
                return <div className={clsx(className, 'bg-primary-gradient')} />;
            }
        }
    };

    if (error) return <FullScreenError />;

    if (!socket || loading)
        return (
            <div className='flex justify-center items-center h-full'>
                <SpinnerLoader text={LOADING_TEXT} />
            </div>
        );

    return (
        <ConversationProvider socket={socket}>
            <ChatContent showHeader={showHeader} renderBackground={renderBackground} />
        </ConversationProvider>
    );
}

export default Conversation;
